// SSO Section Component
// PBI 759: SSO buttons section for AuthPage

import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts';
import { useSSO, SSOProvider } from '../../hooks/useSSO';
import { clearMSALCookies } from '../../utils/clearCookies';
import SSOButton from './SSOButton';

interface SSOSectionProps {
  disabled?: boolean;
  mode?: 'login' | 'signup';
}

interface SSOConfig {
  google: { enabled: boolean; clientId: string };
  microsoft: { enabled: boolean; clientId: string };
}

export const SSOSection: React.FC<SSOSectionProps> = ({ disabled = false, mode = 'login' }) => {
  const { ssoState, authenticateWithSSO, getSSOConfig, clearSSOError } = useSSO();
  const [config, setConfig] = useState<SSOConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const { t } = useLanguage();

  // Load SSO configuration on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const ssoConfig = await getSSOConfig();
        setConfig(ssoConfig);
      } catch (error) {
        // Silently handle SSO config errors
      } finally {
        setConfigLoading(false);
      }
    };

    loadConfig();
  }, [getSSOConfig]);

  // Handle SSO button click
  const handleSSOClick = (provider: SSOProvider, forceAccountSelection: boolean = false) => {
    clearSSOError(provider);
    
    // Clear MSAL cache and localStorage only
    // Note: auth_token cookie is httpOnly and cannot be cleared by frontend
    // It will be cleared by the backend logout endpoint when needed
    clearMSALCookies();
    
    // Clear localStorage items
    localStorage.removeItem('sso_return_url');
    localStorage.removeItem('redirectAfterLogin');
    
    authenticateWithSSO(provider, forceAccountSelection);
  };

  // Loading state
  if (configLoading) {
    return (
      <div className="space-y-3">
        <div className="text-center text-sm text-gray-500">
          <div className="animate-pulse">{t("loadingAuthenticationOptions")}</div>
        </div>
      </div>
    );
  }

  // No SSO providers available
  if (!config || (!config.google.enabled && !config.microsoft.enabled)) {
    return null;
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-gray-500">{t('orContinueWith')}</span>
        </div>
      </div>
      
      <div className="space-y-2">
        {config.google.enabled && (
          <div className="space-y-2">
            <SSOButton
              provider="google"
              onClick={() => handleSSOClick('google')}
              disabled={disabled}
              loading={ssoState.google.loading}
            />
            
            {/* Only show account selection for login mode */}
            {mode === 'login' && (
              <button
                type="button"
                onClick={() => handleSSOClick('google', true)}
                disabled={disabled || ssoState.google.loading}
                className="w-full flex items-center justify-center px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {t('useGoogle')}
              </button>
            )}
            
            {ssoState.google.error && (
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="text-sm text-red-600">
                  <strong>{t("authenticationError")}</strong> {ssoState.google.error}
                </div>
                <button
                  onClick={() => clearSSOError('google')}
                  className="mt-1 text-xs text-red-500 hover:text-red-700 underline"
                >
                  {t("tryAgain")}
                </button>
              </div>
            )}
          </div>
        )}

        {config.microsoft.enabled && (
          <div className="space-y-2">
            <SSOButton
              provider="microsoft"
              onClick={() => handleSSOClick('microsoft')}
              disabled={disabled}
              loading={ssoState.microsoft.loading}
            />

            {ssoState.microsoft.error && (
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="text-sm text-red-600">
                  <strong>{t("authenticationError")}</strong> {ssoState.microsoft.error}
                </div>
                <button
                  onClick={() => clearSSOError('microsoft')}
                  className="mt-1 text-xs text-red-500 hover:text-red-700 underline"
                >
                  {t("tryAgain")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SSOSection;
